import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { migrateLegacyConnectTokenOnce } from './migrateLegacyConnectToken';

// Adopt any pre-split `connect-token` blob before this store hydrates.
migrateLegacyConnectTokenOnce();

// The delegated AcceoConnect / host credentials. Desktop sessions send these as request headers
// (X-Connect-Token / X-Acceo-Session-Token / X-Sso-Bearer); portal sessions hold the AcceoConnect
// token server-side. Distinct from the app session (our own API bearer) in `appSessionStore`.
interface ConnectCredentialsState {
    acceoConnectToken: string | null;
    expiresAt: number | null; // Unix ms — expiry of acceoConnectToken
    saasSessionToken: string | null;
    portalIsToken: string | null; // IS JWT for portal-flow sessions (no OIDC user present)
    setAcceoConnectToken: (acceoConnectToken: string, expiresIn: number) => void;
    setSaasSessionToken: (saasSessionToken: string | null) => void;
    setPortalIsToken: (portalIsToken: string | null) => void;
    clearConnectCredentials: () => void;
    isExpired: () => boolean;
}

// Persisted to localStorage so the Connect session survives a page refresh.
// Trade-off: any XSS vulnerability in the same origin could read these tokens.
// Mitigated by: short expiry checked via isExpired(), SameSite cookie policy on the OIDC session,
// and the fact that this is an internal B2B tool with a known user base.
export const useConnectCredentialsStore = create<ConnectCredentialsState>()(
    persist(
        (set, get) => ({
            acceoConnectToken: null,
            expiresAt: null,
            saasSessionToken: null,
            portalIsToken: null,
            setAcceoConnectToken: (acceoConnectToken: string, expiresIn: number) =>
                set({ acceoConnectToken, expiresAt: Date.now() + expiresIn * 1000 }),
            setSaasSessionToken: (saasSessionToken: string | null) => set({ saasSessionToken }),
            setPortalIsToken: (portalIsToken: string | null) => set({ portalIsToken }),
            clearConnectCredentials: () => set({
                acceoConnectToken: null, expiresAt: null, saasSessionToken: null, portalIsToken: null,
            }),
            isExpired: () => {
                const { expiresAt } = get();
                return expiresAt === null || Date.now() >= expiresAt;
            },
        }),
        { name: 'connect-credentials', version: 0 }
    )
);
